// ============================================================================
// resolveDocIds — the single shared tenant/restaurant docId resolver
// ----------------------------------------------------------------------------
// Numeric business ids (tenantId / restaurantId) map to immutable Firestore
// document ids. Historically ~48 client services each re-implemented this
// resolution inline, so nearly every Firestore-backed call paid two extra
// serial round trips (resolve tenant → resolve restaurant) before its real
// query — the single most repeated avoidable read in the app, and pure
// Firestore cost. This module owns the resolution once.
//
// Design:
//   • Session cache keyed on the (immutable) business id → docId mapping, so
//     the pair resolves ONCE per session instead of per call.
//   • The cache stores the IN-FLIGHT promise (not the resolved string), so
//     concurrent first-load callers coalesce into a single Firestore query
//     instead of stampeding.
//   • not-found → resolves `null` (the entry is evicted so a later-created doc
//     can re-resolve); infra error → the promise REJECTS and is evicted (never
//     poisons the cache, and the real error propagates so it can surface to the
//     user per the error-handling architecture — we never mask an infra failure
//     as a spurious "not found").
//   • The combined `resolveDocIds` throws a clear NotFound error when either id
//     is missing, and accepts already-known docIds (e.g. from SelectionContext)
//     to skip resolution entirely.
//
// This generalizes the session-cache precedent shipped in jobPermissions.ts and
// the resolver pair proven in restaurants.ts / recipeopsFirestore.ts.
// ============================================================================

import { CollectionPaths } from '@order-up/common';
import { Location as LocationRecord, Tenant } from '@order-up/models';
import { FirestoreRepository } from '../interfaces/FirestoreRepository';

// tenantId → Promise<tenantDocId | null>
const tenantDocIdCache = new Map<number, Promise<string | null>>();
// `${tenantDocId}:${restaurantId}` → Promise<restaurantDocId | null>
const locationDocIdCache = new Map<string, Promise<string | null>>();

/**
 * Coalesce concurrent resolutions and cache the in-flight promise. A resolved
 * `null` (not found) and any rejection (infra error) are evicted so they never
 * stick: not-found can re-resolve once the doc exists, and infra errors both
 * propagate to awaiters and leave the cache clean for a retry.
 */
function cachedResolve<K>(
  cache: Map<K, Promise<string | null>>,
  key: K,
  run: () => Promise<string | null>,
): Promise<string | null> {
  const existing = cache.get(key);
  if (existing) return existing;

  const pending = run();
  cache.set(key, pending);
  // Cleanup chain — does NOT swallow the rejection for the cached promise's own
  // awaiters (they await `pending` directly and still receive it).
  void pending.then(
    (docId) => {
      if (docId === null) cache.delete(key);
    },
    () => {
      cache.delete(key);
    },
  );
  return pending;
}

/**
 * Resolve a tenant business id (number) to its Firestore document id.
 * Returns `null` when no tenant matches; lets infra errors propagate.
 */
export const resolveTenantDocId = (tenantId: number): Promise<string | null> =>
  cachedResolve(tenantDocIdCache, tenantId, async () => {
    const tenantRepo = new FirestoreRepository<Tenant>(CollectionPaths.tenants(), Tenant);
    const results = await tenantRepo.find([{ field: 'tenantId', operator: '==', value: tenantId }]);
    return results.length > 0 ? results[0].docId : null;
  });

/**
 * Resolve a restaurant business id (number) to its Firestore document id within
 * the given tenant. Returns `null` when no restaurant matches; lets infra
 * errors propagate.
 */
export const resolveLocationDocId = (
  tenantDocId: string,
  locationNumericId: number,
): Promise<string | null> =>
  cachedResolve(locationDocIdCache, `${tenantDocId}:${locationNumericId}`, async () => {
    const locationRepo = new FirestoreRepository<LocationRecord>(
      CollectionPaths.restaurants(tenantDocId),
      LocationRecord,
    );
    const results = await locationRepo.find([
      { field: 'restaurantId', operator: '==', value: locationNumericId },
    ]);
    return results.length > 0 ? results[0].docId : null;
  });

/**
 * Resolve both ids at once; throws a clear error if either is missing.
 * Pass already-known docIds (e.g. `selection.tenant.docId` /
 * `selection.restaurant.docId` from SelectionContext) via `known` to skip
 * resolution entirely — the values are the same, so this is a zero-network fast
 * path with identical semantics.
 */
export const resolveDocIds = async (
  tenantId: number,
  locationNumericId: number,
  known?: { tenantDocId?: string; restaurantDocId?: string },
): Promise<{ tenantDocId: string; restaurantDocId: string }> => {
  const tenantDocId = known?.tenantDocId ?? (await resolveTenantDocId(tenantId));
  if (!tenantDocId) throw new Error(`Tenant with ID ${tenantId} not found`);

  const locationDocId =
    known?.restaurantDocId ?? (await resolveLocationDocId(tenantDocId, locationNumericId));
  if (!locationDocId) throw new Error(`Restaurant with ID ${locationNumericId} not found`);

  return { tenantDocId, restaurantDocId: locationDocId };
};

/**
 * Clear the session resolver caches. The id→docId mapping is immutable, so this
 * is only needed by tests (and any future hard tenant-context reset).
 */
export const clearResolveDocIdsCache = (): void => {
  tenantDocIdCache.clear();
  locationDocIdCache.clear();
};
